import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Bell, Menu, Plus, Pencil, Search, Trash2 } from 'lucide-react';
import { navItems } from '../../config/navigation';
import { usePageStore } from '../../stores/page-store';
import { useActivityStore, timeAgo, type Activity } from '../../stores/activity-store';
import { cn } from '../../lib/utils';

interface TopBarProps {
  sidebarCollapsed: boolean;
  onMobileMenuOpen: () => void;
}

// ─────────────────────────────────────────────────────────────
// TopBar — Operations Deck (Variation A)
// Layout (single bar, 84px tall):
//   left  → ADMIN · PAGE  +  Page title (Fraunces)
//   right → search · activity bell (recent CRUD) · primary page action
// Pages publish their primary action via `usePageAction(...)`.
// CRUD handlers record entries via `useActivityStore.record(...)`,
// which surface in the bell dropdown.
// ─────────────────────────────────────────────────────────────

function deriveBreadcrumb(pathname: string): { subtitle: string; title: string } {
  const nav = navItems.find((n) => n.path === pathname);
  if (nav) {
    const clean = nav.name.replace(/\s*Management$/i, '');
    return { subtitle: `Administration · ${clean}`, title: clean };
  }
  if (pathname.startsWith('/dashboard/wells/new')) {
    return { subtitle: 'Operations · Wells · New', title: 'New Well' };
  }
  if (pathname.startsWith('/dashboard/wells/edit')) {
    return { subtitle: 'Operations · Wells · Edit', title: 'Edit Well' };
  }
  const last = pathname.split('/').filter(Boolean).pop() ?? 'Dashboard';
  const title = last.charAt(0).toUpperCase() + last.slice(1).replace(/-/g, ' ');
  return { subtitle: `Operations · ${title}`, title };
}

export function TopBar({ sidebarCollapsed, onMobileMenuOpen }: TopBarProps) {
  const { pathname } = useLocation();
  const { subtitle, title } = deriveBreadcrumb(pathname);
  const action = usePageStore((s) => s.action);

  return (
    <header
      className={cn(
        'fixed right-0 top-0 z-30 flex h-topbar items-center gap-4 bg-panel border-b border-border transition-[left] duration-DEFAULT ease-ease',
        sidebarCollapsed ? 'md:left-sidebar-mini' : 'md:left-sidebar',
        'left-0',
      )}
      style={{ paddingLeft: 28, paddingRight: 24 }}
    >
      {/* Mobile hamburger */}
      <button
        onClick={onMobileMenuOpen}
        className="md:hidden flex h-9 w-9 items-center justify-center rounded text-text-mid hover:bg-bg transition-colors duration-fast"
        aria-label="Open menu"
      >
        <Menu size={20} />
      </button>

      {/* ── Left: subtitle + title ────────────────────────────── */}
      <div className="flex-1 min-w-0">
        <div
          className="font-mono uppercase truncate"
          style={{
            fontSize: 10.5,
            letterSpacing: 1.4,
            color: 'var(--color-text-dim)',
            fontWeight: 500,
            lineHeight: 1,
          }}
        >
          {subtitle}
        </div>
        <h1
          className="font-display font-medium text-text truncate"
          style={{ fontSize: 26, letterSpacing: -0.5, lineHeight: 1.15, marginTop: 6 }}
        >
          {title}
        </h1>
      </div>

      {/* ── Right: search · bell · page action ────────────────── */}
      <div className="flex items-center gap-3 flex-shrink-0">
        {/* Search — hidden on small screens */}
        <div
          className="hidden md:flex items-center gap-2 transition-colors duration-fast focus-within:border-accent-bright"
          style={{
            height: 36,
            padding: '0 12px',
            background: 'var(--color-bg)',
            border: '1px solid var(--color-border)',
            borderRadius: 8,
            width: 240,
          }}
        >
          <Search size={14} style={{ color: 'var(--color-text-dim)', flexShrink: 0 }} />
          <input
            placeholder="Search wells, companies…"
            style={{
              flex: 1,
              minWidth: 0,
              background: 'transparent',
              border: 'none',
              outline: 'none',
              fontSize: 12.5,
              color: 'var(--color-text)',
              fontFamily: 'var(--font-body)',
            }}
          />
          <span
            className="font-mono"
            style={{
              fontSize: 10,
              color: 'var(--color-text-dim)',
              border: '1px solid var(--color-border)',
              borderRadius: 4,
              padding: '1px 5px',
              flexShrink: 0,
              background: 'var(--color-panel)',
            }}
          >
            ⌘K
          </span>
        </div>

        {/* Bell with activity dropdown */}
        <NotificationBell />

        {/* Page-specific primary action (from store) */}
        {action}
      </div>
    </header>
  );
}

// ─────────────────────────────────────────────────────────────
// Notification Bell — Option A (session activity log)
// Reads from useActivityStore. Shows up to 12 entries with a
// red dot badge when unread. Click outside / Esc closes it.
// ─────────────────────────────────────────────────────────────

function NotificationBell() {
  const [open, setOpen] = useState(false);
  const popRef = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);

  const activities = useActivityStore((s) => s.activities);
  const unreadCount = useActivityStore((s) => s.unreadCount);
  const markAllRead = useActivityStore((s) => s.markAllRead);
  const clear = useActivityStore((s) => s.clear);

  useEffect(() => {
    if (!open) return;
    markAllRead();
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (popRef.current?.contains(target) || btnRef.current?.contains(target)) return;
      setOpen(false);
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open, markAllRead]);

  return (
    <div style={{ position: 'relative' }}>
      <button
        ref={btnRef}
        onClick={() => setOpen((o) => !o)}
        aria-label="Recent activity"
        title="Recent activity"
        style={{
          width: 36,
          height: 36,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--color-text-mid)',
          background: open ? 'var(--color-bg)' : 'transparent',
          border: '1px solid var(--color-border)',
          borderRadius: 8,
          cursor: 'pointer',
          position: 'relative',
          transition: 'background 120ms',
        }}
        onMouseEnter={(e) => {
          if (!open) e.currentTarget.style.background = 'var(--color-bg)';
        }}
        onMouseLeave={(e) => {
          if (!open) e.currentTarget.style.background = 'transparent';
        }}
      >
        <Bell size={16} />
        {unreadCount > 0 && (
          <span
            style={{
              position: 'absolute',
              top: -3,
              right: -3,
              minWidth: 16,
              height: 16,
              padding: '0 4px',
              borderRadius: 8,
              background: 'var(--color-bad)',
              color: '#fff',
              fontSize: 9.5,
              fontWeight: 700,
              fontFamily: 'var(--font-mono)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 0 0 2px var(--color-panel)',
              lineHeight: 1,
            }}
            aria-label={`${unreadCount} new`}
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          ref={popRef}
          style={{
            position: 'absolute',
            top: 'calc(100% + 8px)',
            right: 0,
            width: 360,
            maxHeight: 480,
            background: 'var(--color-panel)',
            border: '1px solid var(--color-border)',
            borderRadius: 10,
            boxShadow: '0 12px 32px rgba(15,23,32,0.12)',
            zIndex: 60,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <div
            style={{
              padding: '12px 16px',
              borderBottom: '1px solid var(--color-border)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <div>
              <div
                style={{
                  fontSize: 10.5,
                  textTransform: 'uppercase',
                  letterSpacing: 1.2,
                  color: 'var(--color-text-dim)',
                  fontFamily: 'var(--font-mono)',
                  fontWeight: 500,
                }}
              >
                Recent activity
              </div>
              <div style={{ fontSize: 13, color: 'var(--color-text)', fontWeight: 500, marginTop: 2 }}>
                {activities.length === 0
                  ? 'No activity yet'
                  : `Last ${activities.length} action${activities.length === 1 ? '' : 's'}`}
              </div>
            </div>
            {activities.length > 0 && (
              <button
                onClick={clear}
                style={{
                  fontSize: 11.5,
                  color: 'var(--color-text-dim)',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  fontFamily: 'var(--font-body)',
                  padding: '4px 6px',
                  borderRadius: 4,
                }}
                onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--color-bad)')}
                onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--color-text-dim)')}
                title="Clear history"
              >
                Clear
              </button>
            )}
          </div>

          {/* List */}
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {activities.length === 0 ? (
              <div
                style={{
                  padding: '32px 16px',
                  textAlign: 'center',
                  color: 'var(--color-text-dim)',
                  fontSize: 13,
                }}
              >
                <Bell size={28} style={{ margin: '0 auto 8px', opacity: 0.35 }} />
                <div>Actions you take on this device will show up here.</div>
              </div>
            ) : (
              <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                {activities.map((a, i) => (
                  <ActivityItem key={a.id} a={a} isLast={i === activities.length - 1} />
                ))}
              </ul>
            )}
          </div>

          {/* Footer */}
          <div
            style={{
              padding: '8px 16px',
              borderTop: '1px solid var(--color-border)',
              background: 'var(--color-panel-alt)',
              fontSize: 10.5,
              color: 'var(--color-text-dim)',
              fontFamily: 'var(--font-mono)',
              textAlign: 'center',
              letterSpacing: 0.6,
            }}
          >
            SESSION ACTIVITY · STORED LOCALLY
          </div>
        </div>
      )}
    </div>
  );
}

function ActivityItem({ a, isLast }: { a: Activity; isLast: boolean }) {
  const meta = activityMeta(a.type);
  const [, force] = useState(0);
  // Keep the relative time fresh while the popover is open.
  useEffect(() => {
    const t = setInterval(() => force((n) => n + 1), 30_000);
    return () => clearInterval(t);
  }, []);
  return (
    <li
      style={{
        padding: '10px 16px',
        borderBottom: isLast ? 'none' : '1px solid var(--color-border)',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
      }}
    >
      <div
        style={{
          width: 26,
          height: 26,
          borderRadius: 6,
          background: meta.bg,
          color: meta.fg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          marginTop: 1,
        }}
        aria-hidden
      >
        {meta.icon}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, color: 'var(--color-text)', lineHeight: 1.35 }}>
          <span style={{ color: 'var(--color-text-mid)' }}>{meta.verb}</span>{' '}
          <span style={{ fontWeight: 500 }}>{a.label}</span>
        </div>
        <div
          style={{
            fontSize: 11,
            color: 'var(--color-text-dim)',
            fontFamily: 'var(--font-mono)',
            marginTop: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <span style={{ textTransform: 'uppercase', letterSpacing: 0.6 }}>{a.entity}</span>
          <span>·</span>
          <span>{timeAgo(a.timestamp)}</span>
        </div>
      </div>
    </li>
  );
}

function activityMeta(type: Activity['type']): { icon: React.ReactNode; verb: string; bg: string; fg: string } {
  switch (type) {
    case 'create':
      return {
        icon: <Plus size={13} />,
        verb: 'Created',
        bg: 'rgba(26,143,90,0.12)',
        fg: 'var(--color-good)',
      };
    case 'update':
      return {
        icon: <Pencil size={13} />,
        verb: 'Updated',
        bg: 'rgba(11,74,111,0.10)',
        fg: 'var(--color-accent)',
      };
    case 'delete':
      return {
        icon: <Trash2 size={13} />,
        verb: 'Deleted',
        bg: 'rgba(185,28,28,0.10)',
        fg: 'var(--color-bad)',
      };
  }
}
